import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import PageTitle from 'components/UI/PageTitle';
import SegmentedTabs from 'components/UI/SegmentedTabs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NAVY, RADIUS_CARD } from 'styles/tokens';

import type { Integration } from '../../../services/api/integrations.api';
import { useGetIntegrationsQuery } from '../../../services/api/integrations.api';
import ExecutionLogs from './ExecutionLogs';
import MetaStatus from './MetaStatus';
import StatusOverview from './StatusOverview';
import { getStatusLabel } from './utils';

interface IntegrationStatusCounterProps {
  integration: Integration;
  onStatusCalculated: (integrationId: number, statusLabel: string) => void;
}

const IntegrationStatusCounter: React.FC<IntegrationStatusCounterProps> = ({ integration, onStatusCalculated }) => {
  useEffect(() => {
    // Use pre-computed stats from the backend
    const successRate = integration.last24HourStats?.successRate ?? null;
    const statusLabel = getStatusLabel(successRate);
    onStatusCalculated(integration.id, statusLabel);
  }, [
    integration.id,
    // Deep comparison of last24HourStats to detect changes in nested properties
    // Check successRate (primary) and totalExecutions (to detect when stats become available/unavailable)
    integration.last24HourStats?.successRate,
    integration.last24HourStats?.totalExecutions,
    onStatusCalculated,
  ]);

  return null;
};

const IntegrationStatus = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number | null>(null);
  const {
    data: integrations,
    isLoading,
    error,
  } = useGetIntegrationsQuery(undefined, {
    pollingInterval: 150000, // Poll every 2.5 minutes - backend updates every 15 minutes
    refetchOnMountOrArgChange: true,
  });
  const statusMapRef = useRef<Map<number, string>>(new Map());
  const [statusCounts, setStatusCounts] = useState({
    operational: 0,
    degraded: 0,
    majorIssues: 0,
    noData: 0,
    total: 0,
  });

  // Create a serialized key from integrations to detect actual data changes
  const integrationsKey = useMemo(() => {
    if (!integrations || integrations.length === 0) return '';
    return integrations
      .map(
        (int) => `${int.id}:${int.last24HourStats?.successRate ?? 'null'}:${int.last24HourStats?.totalExecutions ?? 0}`
      )
      .join('|');
  }, [integrations]);

  // Recalculate all statuses when integrations data changes
  useEffect(() => {
    if (!integrations || integrations.length === 0) {
      statusMapRef.current.clear();
      setStatusCounts({
        operational: 0,
        degraded: 0,
        majorIssues: 0,
        noData: 0,
        total: 0,
      });
      return;
    }

    // Clear and recalculate all statuses immediately
    statusMapRef.current.clear();
    const counts = {
      operational: 0,
      degraded: 0,
      majorIssues: 0,
      noData: 0,
      total: integrations.length,
    };

    integrations.forEach((integration) => {
      const successRate = integration.last24HourStats?.successRate ?? null;
      const statusLabel = getStatusLabel(successRate);
      statusMapRef.current.set(integration.id, statusLabel);

      switch (statusLabel) {
        case 'Operational':
          counts.operational += 1;
          break;
        case 'Degraded':
          counts.degraded += 1;
          break;
        case 'Major Issues':
          counts.majorIssues += 1;
          break;
        case 'No Data':
          counts.noData += 1;
          break;
        default:
          break;
      }
    });

    setStatusCounts(counts);
  }, [integrationsKey]);

  const handleStatusCalculated = React.useCallback(
    (integrationId: number, statusLabel: string) => {
      statusMapRef.current.set(integrationId, statusLabel);

      // Recalculate counts - use current integrations length to avoid stale closure
      const currentTotal = integrations?.length || 0;
      const counts = {
        operational: 0,
        degraded: 0,
        majorIssues: 0,
        noData: 0,
        total: currentTotal,
      };

      statusMapRef.current.forEach((status) => {
        switch (status) {
          case 'Operational':
            counts.operational += 1;
            break;
          case 'Degraded':
            counts.degraded += 1;
            break;
          case 'Major Issues':
            counts.majorIssues += 1;
            break;
          case 'No Data':
            counts.noData += 1;
            break;
          default:
            break;
        }
      });

      setStatusCounts(counts);
    },
    [integrations]
  );

  const handleIntegrationClick = (integrationId: number) => {
    setSelectedIntegrationId(integrationId);
    setActiveTab(1); // Switch to ExecutionLogs tab
  };

  // Calculate overall status for banner color - MUST be before early returns to follow Rules of Hooks
  const { allOperational, hasErrors, hasDegraded } = useMemo(() => {
    const allOp = statusCounts.operational === statusCounts.total && statusCounts.total > 0;
    const hasErr = statusCounts.majorIssues > 0;
    const hasDeg = statusCounts.degraded > 0;
    return {
      allOperational: allOp,
      hasErrors: hasErr,
      hasDegraded: hasDeg,
    };
  }, [statusCounts]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 3,
        }}
      >
        <Alert severity="error">Error al cargar las integraciones. Por favor, inténtalo más tarde.</Alert>
      </Box>
    );
  }

  const getBannerBgColor = (): string => {
    if (allOperational) return 'success.light';
    if (hasErrors) return 'error.light';
    if (hasDegraded) return 'error.light';
    return 'default.light';
  };

  const getBannerTextColor = (): string => {
    if (allOperational) return 'success.contrastText';
    if (hasErrors) return 'error.contrastText';
    if (hasDegraded) return 'error.contrastText';
    return 'text.primary';
  };

  const getStatusMessage = (): string => {
    if (statusCounts.total === 0) return 'No hay integraciones';

    // Check if we have calculated statuses for all integrations
    const calculatedCount =
      statusCounts.operational + statusCounts.degraded + statusCounts.majorIssues + statusCounts.noData;
    if (calculatedCount === 0 && statusCounts.total > 0) {
      // Still loading, don't show status yet
      return 'Cargando...';
    }

    if (allOperational) return '✓ Todos los Sistemas Operativos';
    if (hasErrors) return '✗ Algunos Sistemas con Problemas';
    if (hasDegraded) return '⚠ Rendimiento Degradado';
    return 'No Data';
  };

  // Ensure stable array for mapping to avoid hook order issues
  const integrationsList = integrations || [];

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      {/* Hidden components to calculate status for each integration */}
      {integrationsList.map((integration) => (
        <IntegrationStatusCounter
          key={integration.id}
          integration={integration}
          onStatusCalculated={handleStatusCalculated}
        />
      ))}
      <Box>
        <PageTitle>Salud Integraciones</PageTitle>

        {/* Overall Status Banner */}
        <Box
          sx={{
            p: 1.5,
            mb: 0,
            borderRadius: RADIUS_CARD,
            bgcolor: getBannerBgColor(),
            color: getBannerTextColor(),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6">{getStatusMessage()}</Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {statusCounts.degraded > 0 && (
                <Chip label={`${statusCounts.degraded} Degraded`} size="small" sx={{ bgcolor: alpha(NAVY, 0.08) }} />
              )}
              {statusCounts.majorIssues > 0 && (
                <Chip
                  label={`${statusCounts.majorIssues} Major Issues`}
                  size="small"
                  sx={{ bgcolor: alpha(NAVY, 0.08) }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Tabs for different views */}
      <Box sx={{ mb: 3 }}>
        <SegmentedTabs
          tabs={['Resumen de Estado', 'Vista Detallada', 'Meta / WhatsApp']}
          value={activeTab}
          onChange={setActiveTab}
        />
      </Box>
      {/* Tab Content */}
      {activeTab === 0 && (
        <StatusOverview integrations={integrations || []} onIntegrationClick={handleIntegrationClick} />
      )}
      {activeTab === 1 && (
        <ExecutionLogs integrations={integrations || []} initialIntegrationId={selectedIntegrationId} />
      )}
      {activeTab === 2 && <MetaStatus />}
    </Box>
  );
};

export default IntegrationStatus;
